/**
 * The transaction spine (docs/05 § offerings, § baskets, § cart-checkout-payment).
 *
 * D1 অফারিং বিস্তার → D2 প্রদানকারী তুলনা → D3 কার্ট (and H3 প্রস্তুত ঝুড়ি) →
 * D4 চেকআউট → D5 পেমেন্ট মেথড → D6 নিশ্চিতকরণ → D7 রসিদ.
 *
 * Every one of those screens was transcribed to the pixel against a fixture module and
 * none of them talked to the API. These are the types that let them, and they are shaped
 * by three rules that are not negotiable anywhere in this flow:
 *
 * 1 · THE CLIENT COMPOSES NO BANGLA. docs/05 § conventions. Every user-visible sentence
 *     in a response is already localised, including the ones that carry a number —
 *     `৳ 1,190 দিয়ে নিশ্চিত করুন` is ONE string from the server, not a client's
 *     `${amount} ${suffix}`. A client that builds a sentence out of a number and a noun
 *     produces grammar no reviewer ever read, in a language most of this codebase's
 *     readers cannot check.
 *
 * 2 · THE CLIENT FORMATS NO MONEY. Every amount crosses the wire as `MoneyDisplay`: the
 *     integer minor units AND the string the server already formatted. See the note on
 *     that type for why the two travel together rather than the client calling
 *     `formatTaka` — which is what every screen in flow D used to do.
 *
 * 3 · THE CLIENT COMPUTES NOTHING. `CheckoutQuoteResponse` extends `CheckoutQuote`
 *     (./api.ts) precisely so that `quoteTotalsAgree` still applies to it: the fee rows
 *     are rendered in the order they arrive, and the total printed is the server's total,
 *     never a sum taken on the screen. A fee the server forgets is a fee the customer
 *     discovers.
 *
 * Runtime checking here is hand-written predicates and asserts, matching `./ai.ts` and
 * `./api.ts`. There is no zod in this package and this file does not add one: the Worker
 * validates request bodies at its own edge (`apps/api/src/http/validate.ts`), and what
 * belongs here is the small set of invariants BOTH sides must agree about.
 */

import type { Currency, FulfilmentMode } from './money.js';
import { assertPartnerDisclosure, formatTaka } from './money.js';
import type { CheckoutQuote, FeeRow } from './api.js';
import { quoteTotalsAgree } from './api.js';

/* ------------------------------------------------------------------ money --- */

/**
 * One amount, twice: the integer the system reasons about and the string a person reads.
 *
 * ── WHY THE STRING IS ON THE WIRE AT ALL ─────────────────────────────────────
 * `formatTaka` is not a formatter, it is a decision — `en-IN` grouping so that
 * ৳ 1,42,000 is not rendered ৳ 142,000 (money.ts § formatTaka carries the whole
 * argument, and that bug shipped once already). A decision taken in a shipped bundle is
 * a decision that cannot be corrected without a deploy of every client, and there will
 * be more than one client. So the server takes it, once, and the screen renders the
 * string it is handed.
 *
 * ── WHICH DIGITS, AND WHY NOT BANGLA ONES ────────────────────────────────────
 * `display` is produced by `formatTaka` — LATIN digits set in Manrope — and not by
 * `formatTakaBn`. That is transcription, not preference:
 *
 *   · docs/02 § numerals keeps amounts in Latin digits in Manrope with tabular figures.
 *   · Every drawn P0 consumer frame agrees: D3 `৳ 1,135`, D4 `৳ 1,190`, D7 `৳ 40`.
 *     Bangla-digit amounts in flow D: zero.
 *   · The visual gate is frozen at 2%, and `৳ ১,১৯০` is a different set of glyphs in a
 *     different face from `৳ 1,190`. Switching digits here would move every money pixel
 *     on seven screens.
 *
 * `formatTakaBn` exists for the founder surfaces, whose frames draw the opposite, and
 * money.ts records that unresolved contradiction at length. If it is ever resolved in
 * favour of Bangla digits for the consumer flow, this is the one line that changes and
 * the seven screens change with it — which is the point of the string being here.
 */
export interface MoneyDisplay {
  amount_minor: number;
  currency: Currency;
  /** Already formatted. The client renders this and never re-derives it. */
  display: string;
}

/** Build a `MoneyDisplay`. The ONE place an amount becomes a string, server-side. */
export function money(amount_minor: number, currency: Currency = 'BDT'): MoneyDisplay {
  return { amount_minor, currency, display: formatTaka(amount_minor) };
}

/** True when the string and the integer are the same amount. */
export function moneyAgrees(m: MoneyDisplay): boolean {
  return m.display === formatTaka(m.amount_minor);
}

/**
 * Refuses a payload whose formatted string disagrees with its own integer.
 *
 * Called on the way out of the Worker, not on the way in to a screen: a receipt showing
 * one number and carrying another is the failure this pair of fields could introduce,
 * and it must be impossible to ship rather than merely unlikely.
 */
export function assertMoneyAgrees(m: MoneyDisplay, where: string): void {
  if (!moneyAgrees(m)) {
    throw new Error(
      `${where}: formatted amount '${m.display}' disagrees with ${String(m.amount_minor)} ` +
        `minor units (expected '${formatTaka(m.amount_minor)}'). The client renders the ` +
        `string, so the two disagreeing means somebody reads a number nobody sent.`,
    );
  }
}

/* -------------------------------------------------------------- D1 · offering --- */

/** One line of D1's "যা করেন" list: what the provider does, and what it costs. */
export interface OfferingService {
  key: string;
  label_bn: string;
  price: MoneyDisplay;
}

/**
 * D1 · অফারিং বিস্তার — `GET /v1/offerings/:id`.
 *
 * `fulfilment` and `partner_name` travel even though D1 draws no disclosure panel:
 * docs/05 § fulfilment requires the RESPONSE to carry the partner on a money-bearing
 * payload, and D1 carries prices. What the screen DISPLAYS is the frame's decision and
 * the frame draws none — reported rather than added, because adding a panel to a drawn
 * screen is the one change the fidelity gate exists to catch.
 */
export interface OfferingResponse {
  id: string;
  provider_id: string;
  provider_name_bn: string;
  provider_meta_bn: string;
  /** `৪.৭ ★` — composed server-side from the provider's rating. */
  rating_bn: string;
  /** `২৩০ কাজ` — composed server-side from the provider's completed jobs. */
  jobs_bn: string;
  eta_bn: string;
  about_bn: string;
  services: OfferingService[];
  visit_fee_label_bn: string;
  /**
   * Null for an offering with no visit fee — a grocery product has none, and D1 is the
   * service screen. The bottom bar then draws its action alone; a `৳ 0` visit fee would
   * be a claim about a price rather than the absence of one.
   */
  visit_fee: MoneyDisplay | null;
  /**
   * `যাচাইকৃত`, or NULL when nothing about this provider has been verified.
   *
   * ── WHY THIS FIELD HAD TO EXIST ──────────────────────────────────────────────
   * D1 drew the badge on every provider. Not because of a bug in a condition — because
   * there was no condition: the word was a Bangla literal inside `D1Offering.tsx` and no
   * verification fact was ever carried to the screen at all.
   *
   * The design does draw the badge on D1. It also draws exactly one provider, and that
   * provider is verified. A frame is a picture of one case; transcribing it as "always
   * render this" turns a specific instance into a universal claim — on the screen a
   * customer reads before letting a stranger into their home.
   *
   * Null, never an empty string, and the screen renders nothing rather than a blank chip.
   * `apps/api/src/provider/types.ts § badge_bn` says the same thing about the same badge
   * on H1, which is where this rule already existed and where D1 should have got it.
   */
  verified_badge_bn: string | null;
  fulfilment: FulfilmentMode;
  partner_name: string | null;
}

/* --------------------------------------------------------------- D2 · compare --- */

/**
 * D2's three sort chips, in the drawn order. The FIRST is the drawn selection.
 *
 * The order is data, not a set: the frame draws দ্রুত আগে · কম দাম · রেটিং left to right
 * and the leftmost is selected, so a client that re-orders them shows a different screen.
 */
export const COMPARE_SORTS = ['fastest', 'cheapest', 'rating'] as const;
export type CompareSort = (typeof COMPARE_SORTS)[number];

export function isCompareSort(value: string): value is CompareSort {
  return (COMPARE_SORTS as readonly string[]).includes(value);
}

export interface CompareSortChip {
  id: CompareSort;
  label_bn: string;
}

export interface CompareProvider {
  id: string;
  name_bn: string;
  meta_bn: string;
  price: MoneyDisplay;
  eta_bn: string;
  /** The soonest arrival is drawn in success green; the rest in muted. */
  eta_tone: 'success' | 'muted';
}

export interface CompareUnavailableProvider {
  id: string;
  name_bn: string;
  /** `সব স্লট পূর্ণ · কাল খালি আছে` — the reason AND the next opening, in one sentence. */
  reason_bn: string;
}

/**
 * D2 · প্রদানকারী তুলনা — `GET /v1/offerings/:id/compare?sort=`.
 *
 * The unavailable providers are part of the RESPONSE, not filtered out of it. The frame
 * note is explicit: "Hiding them makes a thin market look broken rather than busy." A
 * server that returned only what is bookable would make the drawn screen impossible.
 */
export interface CompareResponse {
  offering_id: string;
  title_bn: string;
  sorts: CompareSortChip[];
  selected_sort: CompareSort;
  available: CompareProvider[];
  unavailable_heading_bn: string;
  unavailable: CompareUnavailableProvider[];
}

/* ------------------------------------------------------------------ D3 · cart --- */

export interface CartLine {
  id: string;
  name_bn: string;
  /** `৳ 320 / কেজি` — the unit price and its unit, already one localised string. */
  unit_bn: string;
  quantity: number;
  unit_price: MoneyDisplay;
  line_total: MoneyDisplay;
}

export interface CartSuggestion {
  id: string;
  name_bn: string;
  price: MoneyDisplay;
}

/**
 * The footer both D3 and H3 draw, resolved by the server.
 *
 * `label_bn` is `পণ্যের দাম` when the cart has lines and `কার্ট খালি` when it does not —
 * both drawn, both in this same footer role, and WHICH ONE is a fact about the cart that
 * the server holds. The client renders the string it is given.
 *
 * It is never `মোট`. D3's frame note: "delivery and fees are not in this number yet, and
 * calling it a total here would be the first broken promise."
 */
export interface CartSummary {
  label_bn: string;
  amount: MoneyDisplay;
  line_count: number;
}

/** D3 · কার্ট — `GET /v1/cart`, and the answer to every cart mutation. */
export interface CartResponse {
  cart_id: string;
  lines: CartLine[];
  suggestions: CartSuggestion[];
  summary: CartSummary;
}

/** Bounds on a cart line. A quantity outside these is a client bug, not an order. */
export const MIN_CART_QUANTITY = 1;
export const MAX_CART_QUANTITY = 99;

export interface AddCartItemRequest {
  /** The offering's slug, as the URL carries it — never a numeric row id. */
  offering_id: string;
  quantity: number;
}

export interface UpdateCartItemRequest {
  quantity: number;
}

/* --------------------------------------------------------------- H3 · baskets --- */

export interface PreparedBasket {
  id: string;
  title_bn: string;
  /** `চাল, ডাল, তেল, পেঁয়াজ, ডিম — ৯টি পণ্য` — editorial, not a composed count. */
  contents_bn: string;
  price: MoneyDisplay;
  /** The first basket carries the 3px teal left rule; the second does not. */
  emphasised: boolean;
}

export interface BasketCategory {
  id: string;
  label_bn: string;
}

/**
 * H3 · প্রস্তুত ঝুড়ি — `GET /v1/baskets?category_id=`.
 *
 * One tap fills the cart; removal is per-item. docs/05 § baskets: "Removing from a
 * familiar list is easier than assembling one — that is the whole mechanism." So the
 * response carries the cart summary too: the screen's footer is the cart's footer, and
 * a second round trip to learn the cart is empty would show the footer twice.
 */
export interface BasketsResponse {
  category_id: string;
  title_bn: string;
  baskets_heading_bn: string;
  baskets: PreparedBasket[];
  add_label_bn: string;
  contents_label_bn: string;
  note_bn: string;
  or_heading_bn: string;
  categories: BasketCategory[];
  cart: CartSummary;
}

export interface AddBasketRequest {
  basket_id: string;
}

/* ------------------------------------------------------- D4, D5 · checkout --- */

/** A fee row with its formatted string beside the integer docs/05 already requires. */
export interface CheckoutFeeRow extends FeeRow {
  amount: MoneyDisplay;
}

/** D4's two cards: where it goes and when it arrives. */
export interface CheckoutDestination {
  where_label_bn: string;
  when_label_bn: string;
  change_label_bn: string;
  address_lines_bn: string[];
  slot_bn: string;
}

export interface PaymentMethod {
  id: string;
  label_bn: string;
  /** Second line, drawn only on the অপ্রাপ্য ক্যাশ row: `৳ 1,240.50 আছে`. */
  detail_bn?: string;
}

/**
 * D5 · পেমেন্ট মেথড, which is D4's sheet rather than a screen with its own data.
 *
 * `total_line_bn` is `সর্বমোট ৳ 1,190` as ONE string. The client used to build it from a
 * prefix and a formatted number, which is exactly the composition docs/05 forbids.
 *
 * `ব্যালেন্স` appears nowhere in this flow and cannot: the concept is অপ্রাপ্য ক্যাশ
 * (CLAUDE.md § terminology). `হাতে হাতে দেব` is a listed method in the position the frame
 * gives it, never a fallback appended after the real ones — F4's frame note: "burying it
 * insults that user."
 */
export interface PaymentSheet {
  title_bn: string;
  total_line_bn: string;
  add_card_label_bn: string;
  confirm_label_bn: string;
  backdrop_title_bn: string;
  methods: PaymentMethod[];
}

/**
 * D4 · চেকআউট — `POST /v1/checkout/quote`, the full fee breakdown BEFORE commitment.
 *
 * Extends `CheckoutQuote` deliberately: `quoteTotalsAgree` and `assertPartnerDisclosure`
 * are written against that type and both must apply to what actually reaches a screen.
 */
export interface CheckoutQuoteResponse extends CheckoutQuote {
  fees: CheckoutFeeRow[];
  subtotal: MoneyDisplay;
  total: MoneyDisplay;
  subtotal_label_bn: string;
  total_label_bn: string;
  fees_heading_bn: string;
  refund_link_bn: string;
  /** `৳ 1,190 দিয়ে নিশ্চিত করুন` — the amount is inside the drawn sentence. */
  confirm_label_bn: string;
  /**
   * Null when this cart has no destination yet.
   *
   * D4 draws both cards and nothing else, so there is no drawn state for a checkout
   * reached before an address exists — flagged in `docs/bangla-needed/commerce.md`. A
   * screen renders the two cards when the destination is present and omits them when it
   * is not, rather than drawing an empty card that looks like a loading one.
   */
  destination: CheckoutDestination | null;
  payment: PaymentSheet;
}

/**
 * Everything a money screen must be true about before it paints a figure.
 *
 * It throws rather than degrading. A checkout that renders a total the server did not
 * send, or an amount with no partner named, is the specific failure the fee-disclosure
 * and licensing rules exist to prevent, and there is no recomputation path and no
 * fallback anywhere in flow D.
 */
export function assertCheckoutQuoteRenderable(q: CheckoutQuoteResponse): CheckoutQuoteResponse {
  if (!quoteTotalsAgree(q)) {
    throw new Error(
      'checkout quote disagrees with itself: subtotal + fees ≠ total. The client renders ' +
        'what the server sends and computes nothing (docs/05 § checkout).',
    );
  }
  assertPartnerDisclosure(q);
  assertMoneyAgrees(q.subtotal, 'checkout quote subtotal');
  assertMoneyAgrees(q.total, 'checkout quote total');
  for (const fee of q.fees) assertMoneyAgrees(fee.amount, `checkout fee '${fee.label_bn}'`);
  return q;
}

/* ------------------------------------------------------------ D6, D7 · order --- */

/**
 * Which face a value is set in. Not styling for its own sake: `latin` is Manrope,
 * `bangla` is Anek Bangla, and `money` is Manrope with tabular figures. Manrope carries
 * no Bangla digits and falls back silently at the wrong size (docs/02 § numerals), so
 * the server says which script a value is in rather than the screen guessing from the
 * bytes.
 */
export const ORDER_ROW_SCRIPTS = ['latin', 'bangla', 'money'] as const;
export type OrderRowScript = (typeof ORDER_ROW_SCRIPTS)[number];

export interface OrderRow {
  label_bn: string;
  value: string;
  script: OrderRowScript;
}

/** D6 · নিশ্চিতকরণ. Two exits, no dead end (frame note). */
export interface OrderConfirmation {
  title_bn: string;
  /** Drawn as two lines with a break between them; the break is the frame's, not ours. */
  eta_lines_bn: string[];
  rows: OrderRow[];
  home_label_bn: string;
  view_order_label_bn: string;
}

/** D7 · রসিদ. "It will be forwarded — often to the family member who paid." */
export interface OrderReceipt {
  title_bn: string;
  paid_label_bn: string;
  rows: OrderRow[];
  note_bn: string;
  download_label_bn: string;
  share_label_bn: string;
}

/**
 * One order, in the two presentations the design draws for it.
 *
 * D6 and D7 are the same row read twice — the confirmation a person sees once and the
 * receipt they forward later — so they are one payload with two blocks rather than two
 * endpoints that could drift into disagreeing about an amount.
 */
export interface OrderResponse {
  id: string;
  /** `OP-48219` — the string a person reads aloud over a phone call. */
  reference: string;
  total: MoneyDisplay;
  fulfilment: FulfilmentMode;
  partner_name: string | null;
  /** The activity D6's "অর্ডার দেখুন" opens, or null when none was created. */
  activity_ref: string | null;
  confirmation: OrderConfirmation;
  receipt: OrderReceipt;
}

export interface OrderListResponse {
  orders: OrderResponse[];
}

/**
 * `POST /v1/orders` — the one request in this flow that moves money.
 *
 * It carries an `Idempotency-Key` HEADER supplied by the caller and is deduped
 * server-side through `idempotency_keys` (0015). Nothing in a transport may retry it:
 * retrying is the caller's decision with the caller's key, never a convenience hidden
 * below the call site (apps/web/src/api/client.ts § what this module refuses to do).
 */
export interface PlaceOrderRequest {
  method_key: string;
}

/** The three methods D5 draws, in `transactions.method_key`'s vocabulary (0006). */
export const PAYMENT_METHOD_KEYS = ['oprappo_cash', 'card', 'cash_on_delivery'] as const;
export type PaymentMethodKey = (typeof PAYMENT_METHOD_KEYS)[number];

export function isPaymentMethodKey(value: string): value is PaymentMethodKey {
  return (PAYMENT_METHOD_KEYS as readonly string[]).includes(value);
}

/* ═══════════════════ W2-02 · what a line asks, which domain a cart is in ═══ */
//
// ADDED, NOT EDITED. `index.ts § rules`: this package is append-only during a wave, so
// every shape below either stands alone or EXTENDS the one it grows — the idiom
// `payments.ts § OrderWithPayment` and `§ OfferedPaymentMethod` already use. Every reader
// of `CartResponse`, `OfferingResponse`, `CheckoutQuoteResponse` and `BasketsResponse`
// keeps compiling; the routes send the wider shape, and a reader that asks for the new
// fields gets them.

/** How an offering is priced (0503). */
export const COMMERCE_PRICE_MODES = ['fixed', 'customer_amount', 'quote'] as const;
export type CommercePriceMode = (typeof COMMERCE_PRICE_MODES)[number];

/** What the customer must tell an offering before it can be bought (0503). */
export const COMMERCE_INPUT_KINDS = ['none', 'recharge', 'bill', 'request'] as const;
export type CommerceInputKind = (typeof COMMERCE_INPUT_KINDS)[number];

/**
 * The kinds nothing is delivered for: the operator acts on a number or an account.
 *
 * A cart of these has no destination, no delivery fee, and no cash-on-delivery row — there
 * is no door to hand cash to (founder decision 2026-09-18: recharge is a prepaid manual
 * rail only). Every other kind — including `none`, which is goods and visits — needs to
 * know where and when.
 */
export const DIGITAL_INPUT_KINDS = ['recharge', 'bill'] as const;
export type DigitalInputKind = (typeof DIGITAL_INPUT_KINDS)[number];

export function isDigitalInputKind(kind: string): kind is DigitalInputKind {
  return (DIGITAL_INPUT_KINDS as readonly string[]).includes(kind);
}

/**
 * A Bangladeshi mobile number as the customer types it: `01`, an operator digit 3–9,
 * eight more digits. Eleven digits, no country code, no spaces — the shape every
 * operator's own top-up form takes, and the one an operator here types into an MFS app.
 *
 * The operator is NOT inferred from the prefix. Mobile number portability has been live
 * in Bangladesh since 2018, so `017…` can be a Robi number; the customer says which, and
 * the client may pre-select from the prefix as a convenience (W3-05).
 */
export const BD_MSISDN_PATTERN = /^01[3-9]\d{8}$/;

export function isBdMsisdn(value: string): boolean {
  return BD_MSISDN_PATTERN.test(value);
}

/** The five operators a recharge can go to, as machine keys. */
export const MOBILE_OPERATORS = ['grameenphone', 'robi', 'airtel', 'banglalink', 'teletalk'] as const;
export type MobileOperator = (typeof MOBILE_OPERATORS)[number];

export function isMobileOperator(value: string): value is MobileOperator {
  return (MOBILE_OPERATORS as readonly string[]).includes(value);
}

export const CONNECTION_TYPES = ['prepaid', 'postpaid'] as const;
export type ConnectionType = (typeof CONNECTION_TYPES)[number];

export function isConnectionType(value: string): value is ConnectionType {
  return (CONNECTION_TYPES as readonly string[]).includes(value);
}

/** `{msisdn, operator, connection}` — a recharge line's whole ask (0504). */
export interface RechargeInputs {
  msisdn: string;
  operator: MobileOperator;
  connection: ConnectionType;
}

/**
 * `{biller, account, month}` — a bill line's whole ask (0504).
 *
 * `biller` is a short machine key (`titas`, `desco`); the NAME a person reads is the
 * offering's own title, because there is one offering per biller. `account` is the
 * গ্রাহক নম্বর as printed on the bill. `month` is `YYYY-MM`.
 */
export interface BillInputs {
  biller: string;
  account: string;
  month: string;
}

export type CartItemInputs = RechargeInputs | BillInputs;

export const BILLER_KEY_PATTERN = /^[a-z][a-z0-9-]{1,31}$/;
/** Digits, letters and hyphens, as billers print them. 4–32 characters. */
export const BILL_ACCOUNT_PATTERN = /^[0-9A-Za-z-]{4,32}$/;
/** `YYYY-MM`, month 01–12. */
export const BILL_MONTH_PATTERN = /^(20\d{2})-(0[1-9]|1[0-2])$/;

/**
 * `POST /v1/cart/items`, grown for the offerings that ask something.
 *
 * STILL NO PRICE. `amount_minor` is not a price the caller names for a catalogue row —
 * it is the amount a `customer_amount` offering is FOR (the ৳100 of a ৳100 recharge), and
 * the server refuses it outside the offering's own bounds and on every other price mode.
 * A fixed row's price is read from the catalogue or from the service row `service_key`
 * names, never from the body.
 *
 * `clear_cart` is the other half of a domain mismatch: a cart holds one domain, and the
 * 409 that says so offers to empty the cart and add this line instead. Sending it without
 * a mismatch is harmless and does nothing extra.
 */
export interface AddCartItemWithInputsRequest extends AddCartItemRequest {
  /** An `offering_services.key` of THIS offering — D1's tapped row. */
  service_key?: string;
  /** Poisha. `customer_amount` offerings only, inside their bounds. */
  amount_minor?: number;
  inputs?: CartItemInputs;
  clear_cart?: boolean;
}

/** `POST /v1/cart/basket`, with the same clear-and-add answer to a domain mismatch. */
export interface AddBasketWithClearRequest extends AddBasketRequest {
  clear_cart?: boolean;
}

/** A domain as a screen names it: the id the catalogue routes use, and its label. */
export interface CartDomain {
  id: number;
  label_bn: string;
}

/** One thing the customer told a line, as the line shows it: `নম্বর` · `০১৭১২ ৩৪৫ ৬৭৮`. */
export interface CartLineDetail {
  label_bn: string;
  value_bn: string;
}

/**
 * A cart line that says what it was asked to do.
 *
 * Two recharges of ৳100 are two lines when they go to two numbers (0504), and without
 * `details` they would render as two identical rows. `details` is empty for a line that
 * asks nothing — every drawn line — so D3's pixels do not move.
 */
export interface CartLineWithDetails extends CartLine {
  input_kind: CommerceInputKind;
  service_key: string | null;
  details: CartLineDetail[];
}

/**
 * `GET /v1/cart` and every cart mutation, with the domain the cart is in.
 *
 * `domain` is null for an empty cart and for a cart of lines written before lines knew
 * their domain. `destination_required` is false only when every line is digital — a
 * recharge has no door — and it is what D4 reads to decide whether to draw the two cards.
 */
export interface CartWithDomainResponse extends CartResponse {
  lines: CartLineWithDetails[];
  domain: CartDomain | null;
  destination_required: boolean;
}

/**
 * The 409 a cart answers when a line from another domain is added.
 *
 * NOT an `ApiErrorBody`, deliberately: `ERROR_CODES` is a bijection with the drawn state
 * screens, and "your cart already holds another domain's things" is not a state of a
 * surface — it is a question the offering screen asks in a sheet (W3-03). So it travels
 * as its own code, which the web transport surfaces as `UnmappedApiError` with this body
 * as its payload, and `isCartDomainMismatch` is how a screen recognises it.
 *
 * The copy is AUTHORED (`copy_status`), and the clear-and-add answer is a label the
 * server sends, not one the client writes.
 */
export const CART_DOMAIN_MISMATCH = 'E_CART_DOMAIN_MISMATCH' as const;

export interface CartDomainMismatchBody {
  error: {
    code: typeof CART_DOMAIN_MISMATCH;
    message_bn: string;
    message_en: string;
    reference: string;
  };
  title_bn: string;
  body_bn: string;
  copy_status: 'authored';
  cart_domain: CartDomain | null;
  offering_domain: CartDomain | null;
  /** The button that empties the cart and adds this line: resend with `clear_cart: true`. */
  clear_and_add_label_bn: string;
}

export function isCartDomainMismatch(payload: unknown): payload is CartDomainMismatchBody {
  if (typeof payload !== 'object' || payload === null) return false;
  const error = (payload as { error?: unknown }).error;
  return (
    typeof error === 'object' &&
    error !== null &&
    (error as { code?: unknown }).code === CART_DOMAIN_MISMATCH
  );
}

/**
 * D1, with how the offering is priced and what it asks.
 *
 * A screen that has only `OfferingResponse` cannot tell a recharge (send the customer to
 * the form that asks for a number) from a quote (send them to the request form) from a
 * priced service (বুক করুন). `amount_bounds` is the range a `customer_amount` offering
 * accepts, formatted by the server; null on every other mode.
 */
export interface OfferingWithPricingResponse extends OfferingResponse {
  domain_id: number | null;
  price_mode: CommercePriceMode;
  input_kind: CommerceInputKind;
  amount_bounds: { min: MoneyDisplay; max: MoneyDisplay } | null;
}

/** One line of a basket's contents sheet: what one tap puts in the cart. */
export interface PreparedBasketItem {
  /** The offering's slug. */
  id: string;
  name_bn: string;
  quantity: number;
  /** `৳ 320 / কেজি`, composed by the server exactly as a cart line's. */
  unit_bn: string;
  line_total: MoneyDisplay;
}

export interface PreparedBasketWithItems extends PreparedBasket {
  /** Only what one tap would actually add — an unpriced item is not added, so not listed. */
  items: PreparedBasketItem[];
}

/** H3, with each basket's contents in the payload rather than only its editorial line. */
export interface BasketsWithItemsResponse extends BasketsResponse {
  baskets: PreparedBasketWithItems[];
}

/**
 * D5's sheet, saying so when there is no way to pay.
 *
 * `unavailable_bn` is null whenever `methods` has a row. When it is empty — a recharge
 * before the founders have entered a merchant number (founder slot 1) — it is the one
 * sentence the sheet shows instead of a list, and `POST /v1/orders` answers
 * `E_SERVICE_PAUSED` for the same cart.
 */
export interface PaymentSheetWithAvailability extends PaymentSheet {
  unavailable_bn: string | null;
}

/** D4, resolved for the cart's domain. */
export interface DomainCheckoutQuoteResponse extends CheckoutQuoteResponse {
  domain: CartDomain | null;
  destination_required: boolean;
  payment: PaymentSheetWithAvailability;
}

/** `PUT /v1/cart/destination` — a saved address and a slot of the cart's domain. */
export interface SetCartDestinationRequest {
  address_id: string;
  slot_key: string;
}

/** One slot a domain offers, as its founder wrote it. */
export interface DeliverySlotOption {
  key: string;
  slot_bn: string;
}

/**
 * `GET /v1/cart/destination`, and the answer to `PUT`.
 *
 * `slots` is empty when the cart's domain has no active slot, which today is every domain:
 * a delivery window is a promise somebody has to staff, and none is written for the
 * founders (founder slot). A physical cart with no slot cannot be given a destination, and
 * a physical cart with no destination cannot be ordered — which is the honest state, not a
 * bug to paper over.
 */
export interface CartDestinationResponse {
  required: boolean;
  slots: DeliverySlotOption[];
  selected: { address_id: string | null; slot_key: string | null } | null;
  destination: CheckoutDestination | null;
}
